import React, { FunctionComponent, useMemo } from 'react';
import { cn } from '@companion-professional/webutils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../Dialog';
import { DiagnosisOption } from '../ImageDiagnosis';
import { Button } from '../Button';
import { PainAnalysisDialog, PainAnalysisDialogProps } from './PainAnalysisDialog';
import { ImageDxPointValues, PointWithConditions } from './imageDxTypes';

interface PointWithDialogProps extends PainAnalysisDialogProps {
  point: PointWithConditions;
  options: DiagnosisOption[];

  // displayedOptionValue is the value of the selected option that is displayed in the point.
  displayedOptionValue: number;
  pointValues?: ImageDxPointValues;

  // pageIndex is the index of the page that the point is on.  This is currently only used for testing purposes.
  pageIndex: number;
}

export const PointWithDialog: FunctionComponent<PointWithDialogProps> = ({
  point,
  options,
  displayedOptionValue,
  evaluations,
  onConditionChange,
  onEvaluationsChange,
  currentPointValues,
  pageIndex,
  initialEvaluationValue
}) => {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === displayedOptionValue),
    [displayedOptionValue, options]
  );

  return (
    <Dialog>
      <DialogTrigger asChild data-testid={`${point.name}-point-page-${pageIndex}`}>
        <div
          className={cn(
            'absolute flex flex-row items-center justify-start rounded-md bg-opacity-90 p-1 text-sm text-white'
          )}
          style={{
            top: point.y - 14,
            left: point.x - 14,
            backgroundColor: selectedOption?.color ? selectedOption.color : 'none'
          }}
        >
          <div className="flex cursor-pointer flex-row items-center justify-center gap-1">
            <div
              className={cn(
                'flex size-5 flex-row items-center justify-center rounded-full border border-primary bg-primary/40',
                {
                  'border-secondary bg-widget': selectedOption?.value || selectedOption?.value === 0
                }
              )}
            >
              <span className="text-primary"></span>
            </div>
            <div className="pr-1 text-lg leading-none">{selectedOption?.label}</div>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{point.label}</DialogTitle>
          <DialogDescription>Pain evaluation form</DialogDescription>
        </DialogHeader>
        <div className="h-[450px] overflow-auto rounded-lg border p-4">
          <PainAnalysisDialog
            point={point}
            onConditionChange={onConditionChange}
            onEvaluationsChange={onEvaluationsChange}
            evaluations={evaluations}
            currentPointValues={currentPointValues}
            initialEvaluationValue={initialEvaluationValue}
          />
        </div>
        <DialogFooter className="sm:justify-start">
          <DialogClose asChild>
            <Button size="small" type="button" variant="secondary">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
